import React, {useState, useEffect} from 'react';
import { useFormContext } from '../Provider';
import useValidators from '../Validators/useValidator';
import CommonFieldWrapper from './CommonFieldWrapper';
import Toggle from '../../Button/Toggle';
import Icon from '../../Icons/Icon';
import Grid from '../../Formatting/Grid';
import NoValue from '../../EmptyStates/NoValue';
import Money from '../../Formatting/Money';
import { ItemisationAdjustmentValidator } from '..';
import { classNames } from '../../../Services/helpers';

const fallbackValue = false;
const empty = {amount: 0, description: ''}

const noValidators = [];
let enabledValidators = [];

const ItemisationAdjustment = ({
    label = 'Invoice adjustment',
    toggleLabel = 'One-time invoice adjustment',
    name = 'itemisationAdjustment',
    help,
    onChange,
    disabled,
    footer,
    originalCost,
}) => {
    const { formData, updateField, updateValidity, isValidating, isSubmitting, formDisabled } = useFormContext();
    let value = formData[name] ?? fallbackValue;
    const [enabled, setEnabled] = useState(!!value);
    const [type, setType] = useState(value?.amount < 0 ? 'less' : 'more')

    const validators = (enabled || value) ? enabledValidators : noValidators;
    const {isValid, error} = useValidators(value, validators, name, updateValidity);

    useEffect(() => {
        updateField(name, enabled ? (value ? value : empty) : fallbackValue);
        onChange && onChange(enabled ? (value ? value : empty) : fallbackValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    useEffect(() => {
        if (!value  || value?.amount === 0) {
            return;
        }
        setType(value?.amount >= 0 ? 'more' : 'less');
    }, [value, value?.amount]);

    useEffect(() => {
        if (type === 'more' && value?.amount < 0) {
            handleChange('amount', Math.abs(value.amount));
        } else if (type === 'less' && value?.amount > 0) {
            handleChange('amount', -value.amount);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    useEffect(() => {
        enabledValidators = [ItemisationAdjustmentValidator(originalCost)];
    }, [originalCost]);

    const handleChange = (subfield, val) => {
        const newVal = {
            ...value,
            [subfield]: val
        };

        updateField(name, newVal);
        onChange && onChange(newVal);
    }

    const isDisabled = formDisabled || disabled || isSubmitting;

    return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled, footer}}>
        <Toggle {...{label: toggleLabel, enabled, onChange: setEnabled, disabled: isDisabled}} />

        {value && <TheAdjustment {...{value, type, setType, isDisabled, handleChange, originalCost}} />}

    </CommonFieldWrapper>
}

const TheAdjustment = ({value, type, setType, isDisabled, handleChange, originalCost}) => {

    return <div className="mt-3 flex flex-col gap-3">

        <div className="flex flex-row gap-2">
            {['more', 'less'].map((t) => <div
                key={t}
                className={classNames(
                    'radio',
                    (type === t)
                        ? (isDisabled ? 'radio-checked-disabled' : 'radio-checked-enabled')
                        : (isDisabled ? 'radio-unchecked-disabled' : 'radio-unchecked-enabled'),
                )}
                onClick={() => setType(type === 'more' ? 'less' : 'more')}>
                    Charge {t}
                </div>
            )}
        </div>

        <div className="relative flex flex-row">
            <div className="pointer-events-none inset-y-0 left-0 flex items-center pr-1 fill-gray-400">
                <Icon icon="SterlingSign" size="0.5x" inline />
            </div>

            <input className="ml-1 block w-full border-0 p-0 surface-1 focus:ring-0 sm:text-sm sm:leading-6"
                type="number"
                value={value.amount ? (value.amount/100).toFixed(2) : ''}
                onChange={(e) => {
                    let val = parseInt(e.target.value.replace(/[^\-0-9]*/g, ''));
                    if (e.target.value.indexOf('-') !== -1 && type === 'more') {
                        setType('less');
                    } else if (type === 'less') {
                        val = -Math.abs(val);
                    } else {
                        val = Math.abs(val);
                    }
                    handleChange('amount', val);
                }}
                placeholder={"0.00"}
                disabled={isDisabled}
            />
        </div>

        <div className="relative flex flex-row">
            <input className="block w-full border-0 p-0 surface-1 focus:ring-0 sm:text-sm sm:leading-6"
                type="text"
                // pattern="\d*"
                value={value.description ?? ''}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={isDisabled}
                placeholder="Reason for adjustment (shown on invoice)"
            />
        </div>

        <Grid className="border-t pt-2 text-muted text-sm" items={[
            ['Original cost:', <Money pence={(originalCost ?? 0)} />],
            ['Adjusted cost:', value.amount === null ? <NoValue /> : <Money pence={(originalCost ?? 0) + value.amount} />]
        ]} />

    </div>
}

export default ItemisationAdjustment;
